import { createContext, useContext, useState, useMemo, ReactNode } from "react";

type ProjectExperienceType = "publicLink" | "pinViaEmail" | "passwordProtected";

interface PublishContextValue {
  projectExperienceType: ProjectExperienceType | null;
  setProjectExperienceType: (type: ProjectExperienceType | null) => void;
  emails: string[];
  setEmails: (emails: string[]) => void;
  isEmailValid: boolean;
  setIsEmailValid: (isValid: boolean) => void;
  password: string | null;
  setPassword: (password: string | null) => void;
  isPasswordValid: boolean;
  setIsPasswordValid: (isValid: boolean) => void;
  expirationTime: Date | null;
  setExpirationTime: (expirationTime: Date | null) => void;
}

const PublishContext = createContext<PublishContextValue | undefined>(
  undefined
);

export const PublishProvider = ({ children }: { children: ReactNode }) => {
  const [projectExperienceType, setProjectExperienceType] =
    useState<ProjectExperienceType | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [password, setPassword] = useState<string | null>(null);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [expirationTime, setExpirationTime] = useState<Date | null>(null);

  const value = useMemo(
    () => ({
      projectExperienceType,
      setProjectExperienceType,
      emails,
      setEmails,
      isEmailValid,
      setIsEmailValid,
      password,
      setPassword,
      isPasswordValid,
      setIsPasswordValid,
      expirationTime,
      setExpirationTime,
    }),
    [
      projectExperienceType,
      emails,
      isEmailValid,
      password,
      isPasswordValid,
      expirationTime,
    ]
  );

  return (
    <PublishContext.Provider value={value}>{children}</PublishContext.Provider>
  );
};

export const usePublishContext = () => {
  const context = useContext(PublishContext);
  if (!context) {
    throw new Error("usePublishContext must be used within PublishProvider");
  }
  return context;
};
